import type { Firestore } from 'firebase-admin/firestore'
import type { RedisClient } from '../../core/redis/client.js'
import { EventEmitter } from 'node:events'
import type {
  Challenge,
  ChallengeType,
  ChallengeProgress,
  ChallengeReward,
  ChallengeTier
} from '@my-roadtrip/types'
import { NotFoundError, ValidationError } from '../../common/errors/index.js'
import { createLogger, trackPerformance } from '../../common/utils/logger.js'
import { createBatchBuilder } from '../../common/utils/firestore-batch.js'
import { removeUndefinedFields, safeJsonParse } from '../../utils/sanitize.js'
import type {
  FirestoreData,
  FirestoreDoc,
  FirestoreDocLike,
  FirestoreTransaction
} from '../../common/types/firestore.js'
import { toDateOrNow, toDateOrUndefined } from '../../common/utils/normalize.js'

interface ChallengeTemplate {
  type: ChallengeType
  tier: ChallengeTier
  title: string
  description: string
  targetValue: number
  reward: ChallengeReward
  seasonExclusive?: boolean
}

/**
 * Challenge Service - Handles seasonal challenges, progress tracking, and AI generation
 * 
 * Features:
 * - Season challenge templates (10+ types)
 * - Challenge progress tracking (milestones)
 * - AI challenge concept generation
 * - Tier-based rewards (bronze, silver, gold, platinum)
 * - Redis caching for active challenges
 * - Batch operations for performance
 * - Structured error handling
 */
export class ChallengeService {
  private readonly events: EventEmitter
  // ✅ Increased TTL: 10 min dev, 4 hours prod (challenges rarely change)
  private readonly CACHE_TTL = process.env.NODE_ENV === 'production' ? 14400 : 600
  private readonly SEASON_DURATION = 90 // 90 days per season
  private readonly logger = createLogger('ChallengeService')

  constructor(
    private readonly firestore: Firestore,
    private readonly redis: RedisClient,
    events?: EventEmitter
  ) {
    // Use provided EventEmitter or create new one
    this.events = events || new EventEmitter()
  }

  // ========== CHALLENGE GENERATION ==========

  /**
   * Generate season challenges for a user
   * 
   * @throws {ValidationError} If userId or seasonId is invalid
   */
  async generateSeasonChallenges(userId: string, seasonId: string): Promise<Challenge[]> {
    if (!userId || typeof userId !== 'string') {
      throw new ValidationError('Invalid userId', { userId })
    }
    if (!seasonId || typeof seasonId !== 'string') {
      throw new ValidationError('Invalid seasonId', { seasonId })
    }

    return await trackPerformance(
      'generateSeasonChallenges',
      async () => {
        // Check if user already has season challenges
        const existing = await this.getSeasonChallenges(userId, seasonId)
        if (existing.length > 0) {
          this.logger.debug('User already has season challenges', { userId, seasonId, count: existing.length })
          return existing
        }

        const templates = this.getSeasonChallengeTemplates()
        
        // Select challenges by tier:
        // - 3 bronze (easy)
        // - 2 silver (medium)
        // - 2 gold (hard)
        // - 1 platinum (very hard)
        const bronze = this.selectByTier(templates, 'bronze', 3)
        const silver = this.selectByTier(templates, 'silver', 2)
        const gold = this.selectByTier(templates, 'gold', 2)
        const platinum = this.selectByTier(templates, 'platinum', 1)

        const selected = [...bronze, ...silver, ...gold, ...platinum]

        const challenges: Challenge[] = selected.map(template => ({
          id: '', // Will be set by Firestore
          userId,
          seasonId,
          type: template.type,
          tier: template.tier,
          title: template.title,
          description: template.description,
          targetValue: template.targetValue,
          currentValue: 0,
          progress: {
            current: 0,
            target: template.targetValue,
            milestones: this.generateMilestones(template.targetValue, template.tier)
          },
          status: 'active',
          reward: template.reward,
          seasonExclusive: template.seasonExclusive ?? false,
          startedAt: new Date(),
          expiresAt: this.getSeasonEndDate(),
          createdAt: new Date(),
          updatedAt: new Date()
        }))

        const created = await this.createChallengesBatch(challenges)
        this.logger.info('Generated season challenges', { userId, seasonId, count: created.length })
        return created
      },
      this.logger
    )
  }

  /**
   * Generate AI-powered challenge concepts (experimental)
   */
  async generateAIChallengeIdea(userId: string): Promise<{
    title: string
    description: string
    type: ChallengeType
    targetValue: number
  }> {
    // TODO: Integrate with OpenAI API for custom challenge generation
    // For now, return a random template
    const templates = this.getSeasonChallengeTemplates()
    if (templates.length === 0) {
      throw new NotFoundError('No challenge templates available')
    }

    const templateIndex = this.hashToIndex(userId, templates.length)
    const random = templates[templateIndex]
    
    return {
      title: random.title,
      description: random.description,
      type: random.type,
      targetValue: random.targetValue
    }
  }

  // ========== CHALLENGE TRACKING ==========

  /**
   * Update challenge progress
   * 
   * @throws {ValidationError} If challengeId or increment is invalid
   * @throws {NotFoundError} If challenge not found or not active
   */
  async updateChallengeProgress(
    challengeId: string,
    increment: number
  ): Promise<Challenge | null> {
    if (!challengeId || typeof challengeId !== 'string') {
      throw new ValidationError('Invalid challengeId', { challengeId })
    }
    if (typeof increment !== 'number' || !Number.isFinite(increment) || increment <= 0) {
      throw new ValidationError('Invalid increment value', { increment })
    }

    return await trackPerformance(
      'updateChallengeProgress',
      async () => {
        const challenge = await this.getChallengeById(challengeId)
        if (!challenge) {
          throw new NotFoundError('Challenge not found', { challengeId })
        }
        if (!challenge.status || challenge.status !== 'active') {
          this.logger.warn('Cannot update inactive challenge', { challengeId, status: challenge.status })
          return null
        }

        const newValue = Math.min(challenge.currentValue + increment, challenge.targetValue)
        
        // Check for milestone unlocks
        const unlockedMilestones = this.checkMilestones(
          challenge.progress.milestones,
          challenge.currentValue,
          newValue
        )

        const progress: ChallengeProgress = {
          current: newValue,
          target: challenge.targetValue,
          milestones: challenge.progress.milestones.map(m =>
            m.value <= newValue ? { ...m, unlocked: true } : m
          )
        }

        const updatedAt = new Date()
        await this.firestore
          .collection('challenges')
          .doc(challengeId)
          .update({
            currentValue: newValue,
            progress,
            updatedAt
          })

        // Emit events for milestone unlocks
        for (const milestone of unlockedMilestones) {
          this.events.emit('challenge:milestone', {
            userId: challenge.userId,
            challengeId: challenge.id,
            milestone
          })
        }

        // Check if challenge is completed
        const isCompleted = newValue >= challenge.targetValue
        const completionTimestamp = isCompleted ? updatedAt : null
        if (isCompleted) {
          await this.completeChallenge(challengeId, challenge, completionTimestamp)
        }

        // Invalidate cache
        if (!isCompleted) {
          await this.redis.del(`challenge:${challengeId}`)
          await this.redis.del(`challenges:season:${challenge.userId}:${challenge.seasonId}`)
        }

        this.logger.info('Challenge progress updated', {
          challengeId,
          userId: challenge.userId,
          newValue,
          targetValue: challenge.targetValue,
          completed: isCompleted
        })

        return {
          ...challenge,
          currentValue: newValue,
          progress,
          status: isCompleted ? 'completed' : challenge.status,
          completedAt: isCompleted ? completionTimestamp ?? new Date() : challenge.completedAt
        }
      },
      this.logger
    )
  }

  /**
   * Complete a challenge and award rewards
   */
  async completeChallenge(
    challengeId: string,
    challenge?: Challenge,
    completionTimestamp: Date | null = null
  ): Promise<boolean> {
    const targetChallenge = challenge ?? await this.getChallengeById(challengeId)
    if (!targetChallenge?.status || targetChallenge.status !== 'active') {
      return false
    }
    const completedAt = completionTimestamp ?? new Date()

    // Mark challenge as completed
    await this.firestore
      .collection('challenges')
      .doc(challengeId)
      .update({
        status: 'completed',
        completedAt,
        updatedAt: completedAt
      })

    // Award rewards
    await this.awardChallengeRewards(targetChallenge.userId, targetChallenge.reward)

    // Emit event for other services
    this.events.emit('challenge:completed', {
      userId: targetChallenge.userId,
      challengeId: targetChallenge.id,
      challengeType: targetChallenge.type,
      tier: targetChallenge.tier,
      reward: targetChallenge.reward
    })

    // Invalidate cache
    await this.redis.del(`challenge:${challengeId}`)
    await this.redis.del(`challenges:season:${targetChallenge.userId}:${targetChallenge.seasonId}`)

    return true
  }

  // ========== CHALLENGE RETRIEVAL ==========

  async getUserChallenges(userId: string): Promise<Challenge[]> {
    const snapshot = await this.firestore
      .collection('challenges')
      .where('userId', '==', userId)
      .orderBy('createdAt', 'desc')
      .limit(50)
      .get()

    return snapshot.docs.map((doc: FirestoreDoc) => this.mapDocToChallenge(doc))
  }

  async getSeasonChallenges(userId: string, seasonId: string): Promise<Challenge[]> {
    // Try cache first
    const cacheKey = `challenges:season:${userId}:${seasonId}`
    const cached = await this.redis.get(cacheKey)
    if (cached) {
      const parsed = safeJsonParse<Challenge[]>(cached)
      if (parsed) return parsed
    }

    // Fetch from Firestore
    const snapshot = await this.firestore
      .collection('challenges')
      .where('userId', '==', userId)
      .where('seasonId', '==', seasonId)
      .where('status', '==', 'active')
      .get()

    const challenges = snapshot.docs.map((doc: FirestoreDoc) => this.mapDocToChallenge(doc))

    // Cache for 1 hour
    await this.redis.setex(cacheKey, this.CACHE_TTL, JSON.stringify(challenges))

    return challenges
  }

  async getChallengeById(challengeId: string): Promise<Challenge | null> {
    // Try cache first
    const cacheKey = `challenge:${challengeId}`
    const cached = await this.redis.get(cacheKey)
    if (cached) {
      const parsed = safeJsonParse<Challenge>(cached)
      if (parsed) return parsed
    }

    const doc = await this.firestore.collection('challenges').doc(challengeId).get()
    if (!doc.exists) return null

    const challenge = this.mapDocToChallenge(doc)
    await this.redis.setex(cacheKey, this.CACHE_TTL, JSON.stringify(challenge))

    return challenge
  }

  // ========== SEASON MANAGEMENT ==========

  getCurrentSeasonId(): string {
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth() + 1 // 1-12
    const season = Math.floor((month - 1) / 3) + 1 // 1-4
    return `${year}-S${season}`
  }

  getSeasonEndDate(reference: Date = new Date()): Date {
    const monthsPerSeason = Math.max(1, Math.round(this.SEASON_DURATION / 30))
    const year = reference.getFullYear()
    const month = reference.getMonth() + 1
    const season = Math.floor((month - 1) / monthsPerSeason) + 1

    const endMonth = season * monthsPerSeason
    const endDate = new Date(year, endMonth, 0)
    endDate.setHours(23, 59, 59, 999)

    return endDate
  }

  // ========== MAINTENANCE ==========

  /**
   * Expire old challenges (run at season end)
   */
  async expireSeasonChallenges(seasonId: string): Promise<number> {
    const snapshot = await this.firestore
      .collection('challenges')
      .where('seasonId', '==', seasonId)
      .where('status', '==', 'active')
      .get()

    if (snapshot.empty) return 0

    const batch = createBatchBuilder(this.firestore)
    for (const doc of snapshot.docs) {
      batch.updateRef(doc.ref, {
        status: 'expired',
        updatedAt: new Date()
      })
    }

    await batch.commit()
    return snapshot.size
  }

  // ========== PRIVATE HELPERS ==========

  private async createChallengesBatch(challenges: Challenge[]): Promise<Challenge[]> {
    const batch = createBatchBuilder(this.firestore)
    const fullChallenges: Challenge[] = []

    for (const challenge of challenges) {
      // Remove undefined fields (Challenge has optional: completedAt?)
      const sanitizedChallenge = removeUndefinedFields(challenge) as unknown as Record<string, unknown>
      const id = batch.addSet('challenges', null, sanitizedChallenge)
      fullChallenges.push({ ...challenge, id })
    }

    await batch.commit()
    return fullChallenges
  }

  private async awardChallengeRewards(userId: string, reward: ChallengeReward): Promise<void> {
    const userRef = this.firestore.collection('users').doc(userId)
    
    await this.firestore.runTransaction(async (transaction: FirestoreTransaction) => {
      const userDoc = await transaction.get(userRef)
      if (!userDoc.exists) return

      const currentPoints = userDoc.data()?.points ?? 0
      const currentXP = userDoc.data()?.xp ?? 0

      transaction.update(userRef, {
        points: currentPoints + reward.points,
        xp: currentXP + reward.xp,
        updatedAt: new Date()
      })
    })

    // Award season XP if challenge has seasonXP reward
    if (reward.seasonXP && reward.seasonXP > 0) {
      this.events.emit('season:xp:add', {
        userId,
        xpAmount: reward.seasonXP,
        reason: 'challenge_completion'
      })
    }

    // Emit badge unlock event if badge reward exists
    if (reward.badge) {
      this.events.emit('badge:unlocked', {
        userId,
        badgeId: reward.badge
      })
    }
  }

  private generateMilestones(targetValue: number, tier: ChallengeTier): ChallengeProgress['milestones'] {
    const milestones: ChallengeProgress['milestones'] = []
    const count = tier === 'platinum' ? 5 : tier === 'gold' ? 4 : 3
    
    for (let i = 1; i <= count; i++) {
      const value = Math.floor((targetValue * i) / count)
      const reward = this.getMilestoneReward(tier, i, count)
      
      milestones.push({
        value,
        reward,
        unlocked: false
      })
    }
    
    return milestones
  }

  private getMilestoneReward(tier: ChallengeTier, index: number, total: number): ChallengeProgress['milestones'][0]['reward'] {
    const basePoints = {
      bronze: 50,
      silver: 100,
      gold: 200,
      platinum: 500
    }[tier]

    const progressRatio = index / Math.max(1, total)
    const baseReward = Math.round(basePoints * progressRatio)
    const completionBonus = index === total ? Math.round(basePoints * 0.5) : 0
    const points = Math.max(10, baseReward + completionBonus)

    return {
      points,
      xp: Math.round(points * 0.5)
    }
  }

  private hashToIndex(seed: string, modulo: number): number {
    if (modulo <= 1) return 0
    let hash = 0
    for (const char of seed) {
      hash = (hash * 31 + char.charCodeAt(0)) >>> 0
    }
    return hash % modulo
  }

  private checkMilestones(
    milestones: ChallengeProgress['milestones'],
    oldValue: number,
    newValue: number
  ): ChallengeProgress['milestones'] {
    return milestones.filter(
      m => !m.unlocked && m.value > oldValue && m.value <= newValue
    )
  }

  private selectByTier(templates: ChallengeTemplate[], tier: ChallengeTier, count: number): ChallengeTemplate[] {
    const filtered = templates.filter(t => t.tier === tier)
    const shuffled = [...filtered].sort(() => Math.random() - 0.5)
    return shuffled.slice(0, count)
  }

  private mapDocToChallenge(doc: FirestoreDocLike): Challenge {
    const data = this.requireDocumentData(doc, 'challenge')

    return {
      id: doc.id,
      userId: data.userId,
      seasonId: data.seasonId,
      type: data.type,
      tier: data.tier,
      title: data.title,
      description: data.description,
      targetValue: data.targetValue,
      currentValue: data.currentValue ?? 0,
      progress: data.progress ?? { current: 0, target: data.targetValue ?? 0, milestones: [] },
      status: data.status,
      reward: data.reward ?? { points: 0, xp: 0, seasonXP: 0 },
      seasonExclusive: data.seasonExclusive ?? false,
      startedAt: toDateOrNow(data.startedAt),
      expiresAt: toDateOrNow(data.expiresAt),
      createdAt: toDateOrNow(data.createdAt),
      updatedAt: toDateOrNow(data.updatedAt),
      completedAt: toDateOrUndefined(data.completedAt)
    }
  }

  // ========== CHALLENGE TEMPLATES ==========

  private getSeasonChallengeTemplates(): ChallengeTemplate[] {
    return [
      // BRONZE (Easy)
      {
        type: 'distance_total',
        tier: 'bronze',
        title: 'Season Starter',
        description: 'Drive 1,000 km this season',
        targetValue: 1000,
        reward: { points: 500, xp: 250, seasonXP: 100 }
      },
      {
        type: 'stops_visited',
        tier: 'bronze',
        title: 'Explorer Novice',
        description: 'Visit 50 unique places this season',
        targetValue: 50,
        reward: { points: 600, xp: 300, seasonXP: 120 }
      },
      {
        type: 'plans_created',
        tier: 'bronze',
        title: 'Planner',
        description: 'Create 10 plans this season',
        targetValue: 10,
        reward: { points: 400, xp: 200, seasonXP: 80 }
      },
      
      // SILVER (Medium)
      {
        type: 'distance_total',
        tier: 'silver',
        title: 'Road Warrior',
        description: 'Drive 5,000 km this season',
        targetValue: 5000,
        reward: { points: 2000, xp: 1000, seasonXP: 500 }
      },
      {
        type: 'stops_visited',
        tier: 'silver',
        title: 'Explorer Adept',
        description: 'Visit 200 unique places this season',
        targetValue: 200,
        reward: { points: 2500, xp: 1250, seasonXP: 600 }
      },
      {
        type: 'ai_usage',
        tier: 'silver',
        title: 'AI Enthusiast',
        description: 'Use AI suggestions 100 times this season',
        targetValue: 100,
        reward: { points: 1800, xp: 900, seasonXP: 450, badge: 'ai_enthusiast' }
      },
      
      // GOLD (Hard)
      {
        type: 'distance_total',
        tier: 'gold',
        title: 'Road Master',
        description: 'Drive 15,000 km this season',
        targetValue: 15000,
        reward: { points: 5000, xp: 2500, seasonXP: 1500, badge: 'road_master' }
      },
      {
        type: 'stops_visited',
        tier: 'gold',
        title: 'World Explorer',
        description: 'Visit 500 unique places this season',
        targetValue: 500,
        reward: { points: 6000, xp: 3000, seasonXP: 1800, badge: 'world_explorer' }
      },
      {
        type: 'social_shares',
        tier: 'gold',
        title: 'Social Butterfly',
        description: 'Share 50 plans this season',
        targetValue: 50,
        reward: { points: 4500, xp: 2250, seasonXP: 1350, badge: 'social_butterfly' }
      },
      
      // PLATINUM (Very Hard)
      {
        type: 'distance_total',
        tier: 'platinum',
        title: 'Season Champion',
        description: 'Drive 50,000 km this season',
        targetValue: 50000,
        reward: { points: 20000, xp: 10000, seasonXP: 10000, badge: 'season_champion' },
        seasonExclusive: true
      },
      {
        type: 'stops_visited',
        tier: 'platinum',
        title: 'Ultimate Explorer',
        description: 'Visit 1,000 unique places this season',
        targetValue: 1000,
        reward: { points: 25000, xp: 12500, seasonXP: 12000, badge: 'ultimate_explorer' },
        seasonExclusive: true
      }
    ]
  }

  // ========== EVENT LISTENERS ==========

  on(event: string, listener: (...args: unknown[]) => void): void {
    this.events.on(event, listener)
  }

  off(event: string, listener: (...args: unknown[]) => void): void {
    this.events.off(event, listener)
  }

  private requireDocumentData<T extends FirestoreData>(
    doc: FirestoreDocLike<T>,
    entity: string
  ): T {
    const data = doc.data()
    if (!data) {
      throw new Error(`${entity} document ${doc.id} has no data`)
    }
    return data
  }
}
